import React, { useEffect } from 'react';
//import { logPageView } from '../utils/analytics';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Shield, 
  Eye, 
  Cookie, 
  Database,
  Lock,
  UserCheck,
  FileText,
  Sparkles,
  AlertTriangle
} from 'lucide-react';

const Datenschutz = () => {
  useEffect(() => {
    // SEO Meta Tags
    document.title = 'Datenschutzerklärung - Schteffann IT-Dienstleistungen Mönchengladbach | DSGVO-konform';
    
    //logPageView(); // Or create a logEvent function for custom events

    // Meta Description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Datenschutzerklärung der Schteffann IT-Dienstleistungen. DSGVO-konforme Informationen zum Umgang mit personenbezogenen Daten, Cookies und Google Analytics.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Datenschutzerklärung der Schteffann IT-Dienstleistungen. DSGVO-konforme Informationen zum Umgang mit personenbezogenen Daten, Cookies und Google Analytics.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      const keywords = document.createElement('meta');
      keywords.name = 'keywords';
      keywords.content = 'Datenschutz, DSGVO, Datenschutzerklärung, Cookies, Google Analytics, Schteffann, IT-Dienstleistungen';
      document.getElementsByTagName('head')[0].appendChild(keywords);
    }

    return () => {
      document.title = 'Schteffann - IT-Dienstleistungen für KMU';
    };
  }, []);

  const privacySections = [
    {
      icon: Shield,
      title: "Datenschutz auf einen Blick",
      content: "Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.",
      gradient: "from-cyan-100 to-blue-100"
    },
    {
      icon: UserCheck,
      title: "Verantwortlicher",
      content: "Stephan Schunter, Gladbacher Str. 50, 41179 Mönchengladbach, Deutschland. Telefon: 0156-79697252, E-Mail: kontakt@schteffann.de",
      gradient: "from-blue-100 to-cyan-100"
    },
    {
      icon: Database,
      title: "Datenerfassung",
      content: "Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen (z.B. über das Kontaktformular). Andere Daten werden automatisch beim Besuch der Website durch unsere IT-Systeme erfasst (z.B. Browsertyp, Betriebssystem, Referrer URL).",
      gradient: "from-cyan-100 to-blue-100"
    },
    {
      icon: Eye,
      title: "Ihre Rechte",
      content: "Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Widerspruch und Datenübertragbarkeit. Bei Fragen wenden Sie sich an: kontakt@schteffann.de",
      gradient: "from-blue-100 to-cyan-100"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <section className="relative py-20 lg:py-24 overflow-hidden bg-gradient-to-br from-cyan-50/50 via-blue-50/30 to-slate-50">
        {/* Background Decoration */}
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-600/5"></div>
        <div className="absolute top-10 right-10 w-72 h-72 bg-gradient-to-r from-cyan-400/15 to-blue-500/15 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-gradient-to-r from-blue-400/10 to-cyan-500/10 rounded-full blur-3xl"></div>

        <div className="container relative">
          <div className="text-center mb-4">
            <Badge className="mb-6 bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-cyan-200 hover:from-cyan-200 hover:to-blue-200 px-6 py-2 text-sm font-medium">
              <Shield className="w-4 h-4 mr-2" />
              Rechtliches
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-slate-900">Datenschutz</span><span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600">erklärung</span>
            </h1>
            <p className="text-xl lg:text-2xl text-slate-600 max-w-4xl mx-auto leading-relaxed">
              Informationen zum Umgang mit Ihren personenbezogenen Daten gemäß DSGVO
            </p>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container max-w-6xl mx-auto">
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {privacySections.map((section, index) => {
              const IconComponent = section.icon;
              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-500 border-0 bg-white hover:bg-gradient-to-br hover:from-white hover:to-cyan-50/30 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  <CardHeader className="relative">
                    <div className="flex items-center space-x-4">
                      <div className={`flex items-center justify-center w-16 h-16 bg-gradient-to-br ${section.gradient} rounded-2xl group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                        <IconComponent className="h-8 w-8 text-cyan-600" />
                      </div>
                      <CardTitle className="text-2xl text-slate-900 group-hover:text-cyan-700 transition-colors">
                        {section.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="text-slate-600 leading-relaxed relative">
                    <p>{section.content}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Detailed Sections */}
          <div className="space-y-8">
            {/* Kontaktformular */}
            <Card className="border-0 bg-white shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5"></div>
              <CardHeader className="relative">
                <CardTitle className="text-2xl text-slate-900 flex items-center">
                  <div className="w-2 h-8 bg-gradient-to-b from-cyan-500 to-blue-600 rounded-full mr-4"></div>
                  Kontaktformular
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-600 leading-relaxed relative">
                <p>
                  <strong>Art der Daten:</strong> Name, E-Mail-Adresse, Unternehmen, Telefonnummer, Betreff, Nachricht
                </p>
                <p>
                  <strong>Zweck:</strong> Bearbeitung Ihrer Anfrage und für mögliche Rückfragen
                </p>
                <p>
                  <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragsanbahnung) oder Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse)
                </p>
                <p>
                  <strong>Speicherdauer:</strong> Bis zur vollständigen Bearbeitung Ihrer Anfrage, anschließend 3 Jahre für mögliche Gewährleistungsansprüche
                </p>
              </CardContent>
            </Card>

            {/* Google Analytics */}
            <Card className="border-0 bg-white shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-cyan-500/5"></div>
              <CardHeader className="relative">
                <CardTitle className="text-2xl text-slate-900 flex items-center">
                  <div className="w-2 h-8 bg-gradient-to-b from-blue-500 to-cyan-600 rounded-full mr-4"></div>
                  Google Analytics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 text-slate-600 leading-relaxed relative">
                <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-6 rounded-xl border border-cyan-100">
                  <div className="flex items-center space-x-3 mb-3">
                    <AlertTriangle className="h-6 w-6 text-cyan-600" />
                    <strong className="text-slate-800">Wichtiger Hinweis:</strong>
                  </div>
                  <p className="text-slate-700">
                    Google Analytics wird nur aktiviert, wenn Sie der Verwendung von Analyse-Cookies über unser Cookie-Banner zugestimmt haben.
                  </p>
                </div>
                
                <div>
                  <h4 className="font-bold text-slate-800 mb-3">Was ist Google Analytics?</h4>
                  <p>
                    Diese Website benutzt Google Analytics, einen Webanalysedienst der Google Ireland Limited („Google"). 
                    Google Analytics verwendet sog. „Cookies", Textdateien, die auf Ihrem Computer gespeichert werden und 
                    die eine Analyse der Benutzung der Website durch Sie ermöglichen.
                  </p>
                </div>
                
                <div>
                  <h4 className="font-bold text-slate-800 mb-3">IP-Anonymisierung</h4>
                  <p>
                    Wir haben auf dieser Website die Funktion IP-Anonymisierung aktiviert. Dadurch wird Ihre IP-Adresse 
                    von Google innerhalb von Mitgliedstaaten der Europäischen Union oder in anderen Vertragsstaaten des 
                    Abkommens über den Europäischen Wirtschaftsraum vor der Übermittlung in die USA gekürzt.
                  </p>
                </div>
                
                <div>
                  <h4 className="font-bold text-slate-800 mb-3">Widerspruch gegen Datenerfassung</h4>
                  <p>
                    Sie können die Erfassung Ihrer Daten durch Google Analytics verhindern, indem Sie auf den Cookie-Banner 
                    auf unserer Website die Analyse-Cookies deaktivieren oder das Browser-Add-on zur Deaktivierung von Google Analytics 
                    herunterladen und installieren.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Cookies */}
            <Card className="border-0 bg-white shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5"></div>
              <CardHeader className="relative">
                <CardTitle className="text-2xl text-slate-900 flex items-center">
                  <Cookie className="h-8 w-8 text-cyan-600 mr-4" />
                  Cookies
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 text-slate-600 leading-relaxed relative">
                <p>
                  Diese Website verwendet Cookies. Das sind kleine Textdateien, die Ihr Webbrowser auf 
                  Ihrem Endgerät speichert. Cookies helfen uns dabei, unser Angebot nutzerfreundlicher, 
                  effektiver und sicherer zu machen.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border border-cyan-100">
                    <h4 className="font-bold text-slate-800 mb-2 flex items-center">
                      <Lock className="h-5 w-5 text-cyan-600 mr-2" />
                      Technisch notwendige Cookies
                    </h4>
                    <p className="text-sm">
                      Diese Cookies sind erforderlich, damit Sie durch die Seiten navigieren und 
                      wesentliche Funktionen nutzen können.
                    </p>
                  </div>
                  
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-100">
                    <h4 className="font-bold text-slate-800 mb-2 flex items-center">
                      <Eye className="h-5 w-5 text-blue-600 mr-2" />
                      Analyse-Cookies
                    </h4>
                    <p className="text-sm">
                      Diese Cookies sammeln Informationen darüber, wie Sie unsere Website nutzen. 
                      Diese werden nur gesetzt, wenn Sie zugestimmt haben.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Ihre Rechte im Detail */}
            <Card className="border-0 bg-gradient-to-br from-cyan-50 to-blue-50 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5"></div>
              <CardHeader className="relative">
                <CardTitle className="text-2xl text-slate-900 flex items-center">
                  <UserCheck className="h-8 w-8 text-cyan-600 mr-4" />
                  Ihre Rechte im Detail
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-600 leading-relaxed relative">
                <p className="font-medium text-slate-800 mb-4">Sie haben folgende Rechte bezüglich Ihrer personenbezogenen Daten:</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { title: "Recht auf Auskunft", desc: "Sie können Auskunft über Ihre von uns verarbeiteten personenbezogenen Daten verlangen." },
                    { title: "Recht auf Berichtigung", desc: "Sie haben das Recht, unverzüglich die Berichtigung unrichtiger oder unvollständiger personenbezogener Daten zu verlangen." },
                    { title: "Recht auf Löschung", desc: "Sie können die Löschung Ihrer personenbezogenen Daten verlangen." },
                    { title: "Recht auf Einschränkung", desc: "Sie haben das Recht, die Einschränkung der Verarbeitung zu verlangen." },
                    { title: "Recht auf Widerspruch", desc: "Sie haben das Recht, aus Gründen, die sich aus Ihrer besonderen Situation ergeben, jederzeit gegen die Verarbeitung Widerspruch einzulegen." },
                    { title: "Recht auf Datenübertragbarkeit", desc: "Sie haben das Recht, Ihre Daten in einem strukturierten, gängigen und maschinenlesbaren Format zu erhalten." }
                  ].map((right, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-sm border border-cyan-100 hover:shadow-md transition-shadow">
                      <h5 className="font-bold text-slate-800 mb-2 text-sm">{right.title}</h5>
                      <p className="text-xs text-slate-600">{right.desc}</p>
                    </div>
                  ))}
                </div>
                
                <div className="bg-gradient-to-r from-cyan-100 to-blue-100 p-6 rounded-xl border border-cyan-200 mt-6">
                  <p className="font-bold text-slate-800 mb-2">Kontakt zur Ausübung Ihrer Rechte:</p>
                  <p className="text-slate-700">
                    E-Mail: <a href="mailto:kontakt@schteffann.de" className="text-cyan-600 hover:text-blue-600 font-medium">kontakt(@)schteffann.de</a><br />
                    Telefon: <a href="tel:015679697252" className="text-cyan-600 hover:text-blue-600 font-medium">0156-79697252</a>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* SSL-Verschlüsselung */}
            <Card className="border-0 bg-white shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-cyan-500/5"></div>
              <CardHeader className="relative">
                <CardTitle className="text-2xl text-slate-900 flex items-center">
                  <Lock className="h-8 w-8 text-blue-600 mr-4" />
                  SSL- bzw. TLS-Verschlüsselung
                </CardTitle>
              </CardHeader>
              <CardContent className="text-slate-600 leading-relaxed relative">
                <p>
                  Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher 
                  Inhalte, wie zum Beispiel Bestellungen oder Anfragen, die Sie an uns als Seitenbetreiber 
                  senden, eine SSL- bzw. TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie 
                  daran, dass die Adresszeile des Browsers von „http://" auf „https://" wechselt und an 
                  dem Schloss-Symbol in Ihrer Browserzeile.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-16 text-center">
            <Badge className="bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-cyan-200 px-6 py-3">
              <Sparkles className="w-4 h-4 mr-2" />
              Stand: Juni 2025
            </Badge>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Datenschutz;